/**
 * The generic claim mutations, one hook each — the web half of the
 * /earn/* capability endpoints. All keyed by objectiveTemplateId: the card
 * config names the objective, the member supplies the payload, the pipeline
 * resolves the score. Nothing campaign-shaped here.
 *
 * One file for the four because they are the same six lines with a different
 * operation — a file per hook would be the premature-abstraction rule's
 * mirror image (ceremony per variant instead of a helper per use).
 */
import {useMutation, useQuery} from "@tanstack/react-query";
import {EarnApi} from "@devour/client";
import getConfig from "@/utils/getConfig";
import {useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";

const useEarnApi = (): EarnApi => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    return new EarnApi(getConfig(fullToken));
};

/** Serial / registered-product claim — verified through the objective's configured vendor adapter. */
export function useVerifyEarnSerial() {
    const api = useEarnApi();
    return useMutation({
        mutationFn: async (body: { objectiveTemplateId: string; serialNumber: string }) =>
            await api.createEarnSerialVerify({earnSerialVerifyBody: body}),
    });
}

/** Social follow claim — the platform followed, policy-checked server-side. */
export function useCreateEarnSocialFollow() {
    const api = useEarnApi();
    return useMutation({
        mutationFn: async (body: { objectiveTemplateId: string; platform: string }) =>
            await api.createEarnSocialFollow({earnSocialFollowBody: body}),
    });
}

/** Social like claim — the liked post's URL, matched against the assignment's targets. */
export function useCreateEarnSocialLike() {
    const api = useEarnApi();
    return useMutation({
        mutationFn: async (body: { objectiveTemplateId: string; postUrl: string }) =>
            await api.createEarnSocialLike({earnSocialLikeBody: body}),
    });
}

/** Social post claim — "paste the link, we check it": content-verified server-side. */
export function useCreateEarnSocialPost() {
    const api = useEarnApi();
    return useMutation({
        mutationFn: async (body: { objectiveTemplateId: string; postUrl: string; platform: string }) =>
            await api.createEarnSocialPost({earnSocialPostBody: body}),
    });
}

/** Referral claim — an invite by email, capped and templated by the assignment's policy. */
export function useCreateEarnReferral() {
    const api = useEarnApi();
    return useMutation({
        mutationFn: async (body: {
            objectiveTemplateId: string;
            recipientEmail: string;
            recipientFirstName?: string;
            recipientLastName?: string;
            fromName?: string;
        }) => api.createEarnReferral({earnReferralBody: body}),
    });
}

/**
 * What the claim form renders itself from: the assignment's accepted
 * platforms, required hashtags, target posts, instruction copy and the
 * member's remaining claims this period.
 *
 * The query key is exported because every claim mutation invalidates it —
 * a successful claim changes the remaining count the form is showing.
 */
export const earnActionPolicyKey = (objectiveTemplateId: string): [string, string] =>
    ["earn-action-policy", objectiveTemplateId];

export function useGetEarnActionPolicy(objectiveTemplateId: string) {
    const api = useEarnApi();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    return useQuery({
        queryKey: earnActionPolicyKey(objectiveTemplateId),
        queryFn: async () => api.getEarnClaimPolicyObjectiveTemplateId({objectiveTemplateId}),
        /* Anonymous members see the form's copy on sign-in, not before. */
        enabled: Boolean(fullToken && objectiveTemplateId),
        staleTime: 60_000,
    });
}
